import React, { useState, useEffect } from 'react';
import { MaintenanceRequest, MaintenanceCompany } from '../../types';
import { api } from '../../services/api';
import { Wrench, CheckCircle, Clock, AlertTriangle, User, Calendar, DollarSign, Edit } from 'lucide-react';
import MaintenanceForm from '../MaintenanceForm'; // Reusing existing form for creation
// We need a new modal for updating status/assigning company

const MaintenanceRequests: React.FC = () => {
    const [requests, setRequests] = useState<MaintenanceRequest[]>([]);
    const [companies, setCompanies] = useState<MaintenanceCompany[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState<MaintenanceRequest | null>(null);
    
    // Update State
    const [updateData, setUpdateData] = useState<{
        status: string;
        companyId: string;
        finalCost: number;
        completionNotes: string;
    }>({ status: '', companyId: '', finalCost: 0, completionNotes: '' });

    const formatDate = (dateStr: string | null | undefined) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return isNaN(date.getTime()) ? '-' : date.toLocaleDateString('ar-KW');
    };

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const [reqs, comps] = await Promise.all([
                api.maintenance.getAll(),
                api.maintenance.companies.getAll()
            ]);
            setRequests(reqs);
            setCompanies(comps);
        } catch (error) {
            console.error('Failed to load maintenance data', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateSubmit = async (data: MaintenanceRequest) => {
        try {
            await api.maintenance.create(data);
            setShowCreateForm(false);
            loadData();
            alert('تم إنشاء الطلب بنجاح');
        } catch (error) {
            alert('فشل إنشاء الطلب');
        }
    };

    const openUpdateModal = (req: MaintenanceRequest) => {
        setSelectedRequest(req);
        setUpdateData({
            status: req.status,
            companyId: req.companyId || '',
            finalCost: req.finalCost || 0,
            completionNotes: req.completionNotes || ''
        });
    };

    const handleUpdateSubmit = async () => {
        if (!selectedRequest) return;
        try {
            // Format date for MySQL: YYYY-MM-DD HH:MM:SS
            const now = new Date();
            const formattedDate = now.toISOString().slice(0, 19).replace('T', ' ');

            await api.maintenance.updateStatus(selectedRequest.id, {
                status: updateData.status,
                companyId: updateData.companyId ? parseInt(updateData.companyId) : null,
                cost: Number(updateData.finalCost) || 0,
                completionNotes: updateData.completionNotes,
                completionDate: updateData.status === 'Completed' ? formattedDate : null
            });
            setSelectedRequest(null);
            loadData();
        } catch (error) {
            alert('فشل تحديث الطلب');
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Completed': return 'bg-green-100 text-green-700';
            case 'In Progress': return 'bg-blue-100 text-blue-700';
            default: return 'bg-amber-100 text-amber-700';
        }
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'High': return 'text-red-600 bg-red-50 border-red-200';
            case 'Medium': return 'text-amber-600 bg-amber-50 border-amber-200';
            default: return 'text-slate-600 bg-slate-50 border-slate-200';
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                 <h2 className="text-xl font-bold text-slate-800">طلبات الصيانة</h2>
                <button 
                    onClick={() => setShowCreateForm(true)}
                    className="bg-fly-600 text-white px-4 py-2 rounded-xl flex items-center gap-2 hover:bg-fly-700 transition-all font-bold shadow-lg shadow-fly-600/20"
                >
                    <Wrench size={18} />
                    طلب صيانة جديد
                </button>
            </div>

            {isLoading ? (
                <div className="flex justify-center p-10"><div className="w-8 h-8 border-4 border-fly-200 border-t-fly-600 rounded-full animate-spin"></div></div>
            ) : (
                <div className="space-y-4">
                    {requests.map(req => (
                        <div key={req.id} className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-all">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <div className="flex items-center gap-3 mb-2">
                                        <h3 className="font-bold text-lg text-slate-800">{req.propertyName} {req.unitNumber && `- وحدة ${req.unitNumber}`}</h3>
                                        <span className={`px-3 py-1 rounded-full text-xs font-bold border ${getPriorityColor(req.priority)}`}>
                                            {req.priority === 'High' ? 'طارئ' : req.priority === 'Medium' ? 'متوسط' : 'عادي'}
                                        </span>
                                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(req.status)}`}>
                                            {req.status === 'Completed' ? 'تم الإنجاز' : req.status === 'In Progress' ? 'جاري العمل' : 'قيد الانتظار'}
                                        </span>
                                    </div>
                                    <p className="text-slate-600 font-medium mb-2">{req.description}</p>
                                    <div className="flex items-center gap-4 text-sm text-slate-500">
                                        <span className="flex items-center gap-1"><Calendar size={14}/> {formatDate(req.dateReported)}</span>
                                        {req.companyName && <span className="flex items-center gap-1 text-blue-600 font-bold"><User size={14}/> {req.companyName}</span>}
                                    </div>
                                </div>
                                <button onClick={() => openUpdateModal(req)} className="text-slate-400 hover:text-fly-600 p-2 hover:bg-fly-50 rounded-lg transition-colors">
                                    <Edit size={20} />
                                </button>
                            </div>
                            
                            {/* Completion Details */}
                            {req.status === 'Completed' && (
                                <div className="mt-4 pt-4 border-t border-slate-100 bg-slate-50/50 -mx-6 -mb-6 p-4 px-6 rounded-b-2xl">
                                    <div className="flex flex-wrap gap-6 text-sm">
                                        {req.completionDate && (
                                            <div className="flex items-center gap-2 text-green-700">
                                                <CheckCircle size={16} />
                                                <span className="font-bold">تاريخ الإنجاز:</span>
                                                {formatDate(req.completionDate)}
                                            </div>
                                        )}
                                        {req.finalCost ? (
                                             <div className="flex items-center gap-2 text-slate-700">
                                                <DollarSign size={16} />
                                                <span className="font-bold">التكلفة النهائية:</span>
                                                {req.finalCost} د.ك
                                            </div>
                                        ) : null}
                                         {req.completionNotes && (
                                             <div className="w-full mt-2 text-slate-600">
                                                <span className="font-bold block mb-1">ملاحظات الإنجاز:</span>
                                                {req.completionNotes}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                     {requests.length === 0 && (
                        <div className="text-center py-10 text-slate-400">لا توجد طلبات صيانة</div>
                    )}
                </div>
            )}

            {/* Create Modal */}
            {showCreateForm && (
                <MaintenanceForm onCancel={() => setShowCreateForm(false)} onSubmit={handleCreateSubmit} />
            )}

            {/* Update Modal */}
            {selectedRequest && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn overflow-hidden">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh] border border-slate-200 animate-scaleIn">
                        <div className="bg-white p-6 border-b border-slate-200 flex justify-between items-center shrink-0">
                            <h2 className="text-xl font-black text-slate-800 flex items-center gap-2">
                                <div className="bg-fly-50 p-2 rounded-lg">
                                    <Wrench className="w-5 h-5 text-fly-600" />
                                </div>
                                <span>تحديث حالة الطلب</span>
                            </h2>
                            <button onClick={() => setSelectedRequest(null)} className="p-2 bg-slate-50 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all border border-slate-100">✕</button>
                        </div>
                        
                        <div className="flex-1 overflow-y-auto p-6 space-y-5 custom-scrollbar bg-slate-50/50">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1.5">الحالة</label>
                                <select className="w-full border border-slate-300 rounded-xl px-4 py-3 bg-white text-slate-900 focus:outline-none focus:border-fly-500 focus:ring-4 focus:ring-fly-500/10 transition-all shadow-sm"
                                    value={updateData.status} onChange={e => setUpdateData({...updateData, status: e.target.value})}>
                                    <option value="Pending">قيد الانتظار</option>
                                    <option value="In Progress">جاري العمل</option>
                                    <option value="Completed">تم الإنجاز</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1.5">إسناد إلى شركة صيانة</label>
                                <select className="w-full border border-slate-300 rounded-xl px-4 py-3 bg-white text-slate-900 focus:outline-none focus:border-fly-500 focus:ring-4 focus:ring-fly-500/10 transition-all shadow-sm"
                                    value={updateData.companyId} onChange={e => setUpdateData({...updateData, companyId: e.target.value})}>
                                    <option value="">-- بدون شركة --</option>
                                    {companies.map(c => <option key={c.id} value={c.id}>{c.name} ({c.specialty})</option>)}
                                </select>
                            </div>

                            {updateData.status === 'Completed' && (
                                <div className="space-y-4 p-4 bg-green-50 rounded-2xl border border-green-100">
                                    <h4 className="font-bold text-green-800 flex items-center gap-2 text-sm"><CheckCircle size={16}/> تفاصيل الإنجاز</h4>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 mb-1">التكلفة النهائية (د.ك)</label>
                                        <input type="number" min="0" step="0.001" className="w-full border border-slate-300 rounded-xl px-4 py-2.5 bg-white text-slate-900 focus:outline-none focus:border-fly-500 focus:ring-4 focus:ring-fly-500/10 transition-all shadow-sm"
                                            value={updateData.finalCost} onChange={e => setUpdateData({...updateData, finalCost: Number(e.target.value)})} />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 mb-1">ملاحظات الإنجاز</label>
                                        <textarea className="w-full border border-slate-300 rounded-xl px-4 py-2.5 bg-white text-slate-900 focus:outline-none focus:border-fly-500 focus:ring-4 focus:ring-fly-500/10 transition-all shadow-sm" rows={3}
                                            value={updateData.completionNotes} onChange={e => setUpdateData({...updateData, completionNotes: e.target.value})}></textarea>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="p-4 bg-white border-t border-slate-200 flex justify-end gap-3 shrink-0">
                            <button onClick={() => setSelectedRequest(null)} className="px-6 py-2.5 border border-slate-300 text-slate-700 font-bold rounded-xl hover:bg-slate-50 transition-all text-xs font-bold">إلغاء</button>
                            <button onClick={handleUpdateSubmit} className="px-8 py-2.5 bg-fly-600 text-white rounded-xl font-bold hover:bg-fly-700 shadow-lg shadow-fly-600/20 transition-all text-xs font-bold">حفظ التحديث</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MaintenanceRequests;
